import { useCallback, useEffect, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import CreatableSelect from 'react-select/creatable';
import {
  appendToken,
  FilterColumn,
  makeFilter,
  Option,
  parseTokensBasicToAdvanced,
  removeToken
} from '@blisspointmedia/bpm-types/dist/FilterBar';
import { useDebouncedCallback } from '../../utils/hooks/useDebouncedCallback';
import { Size, SizeType } from '../designConstants';
import { IconButton } from '../IconButton';
import { BASIC_FILTER_STORAGE_KEY, useFilterBarTokens } from './useFilterBarTokens';

const FILTER_DEBOUNCE_MS = 250;

interface BasicFilterBarProps<T = object> {
  defaultTokens?: Option[];
  disableAutoFilter?: boolean;
  initialValue?: string;
  onChange?: (newTokens: Option[]) => void;
  onFilter: (filter: (line: T) => boolean, tokens: Option[]) => void;
  options: string[] | FilterColumn[];
  placeholder?: string;
  size?: SizeType;
  // Disambiguates the sessionStorage key when a page renders more than one filterable table at the
  // same pathname -- see useFilterBarTokens.ts.
  storageKeySuffix?: string;
  tokens?: Option[];
  useLocalStorage?: boolean;
  filterOnModeChange?: boolean;
}

export const BasicFilterBar = <T extends object>({
  defaultTokens = [],
  disableAutoFilter,
  initialValue,
  onChange = (_newTokens: Option[]) => {},
  onFilter = (_filter: (line: T) => boolean, _tokens: Option[]) => {},
  options,
  placeholder = 'Search',
  size = Size.LG,
  storageKeySuffix,
  tokens,
  useLocalStorage = true,
  filterOnModeChange = true
}: BasicFilterBarProps<T>) => {
  const [initialized, setInitialized] = useState(false);

  const triggerFilter = useCallback(
    (filterTokens: Option[]) => {
      const fakeTokens = parseTokensBasicToAdvanced(filterTokens, options);
      const resultFilter = makeFilter(fakeTokens, options);
      if (filterOnModeChange || initialized) {
        onFilter(resultFilter, fakeTokens);
      }
      setInitialized(true);
    },
    [options, onFilter, initialized, filterOnModeChange]
  );

  const { input, setInput, ourTokens, setTokens } = useFilterBarTokens({
    defaultTokens,
    disableAutoFilter,
    initialValue,
    onChange,
    onAutoFilter: triggerFilter,
    storageKey: BASIC_FILTER_STORAGE_KEY,
    storageKeySuffix,
    tokens,
    useLocalStorage
  });

  // On mount, do it! We only want this to run on mount, so we don't want it to rerun whenever the tokens or anything else changes
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    triggerFilter(ourTokens);
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Debounce so each keystroke doesn't trigger a full filter rebuild. Enter,
  // Tab, and the submit button cancel the timer and fire immediately.
  const [scheduleFilter, cancelPendingFilter] = useDebouncedCallback(
    triggerFilter,
    FILTER_DEBOUNCE_MS
  );

  // Turns a selected option ('value') into a filter token
  const addOption = useCallback(
    (value: string) => {
      setInput('');
      setTokens(appendToken(ourTokens, { value, label: value }));
    },
    [ourTokens, setInput, setTokens]
  );

  // Removes the specified selected option / token ('value')
  const removeOption = useCallback(
    (value: string | undefined) => {
      setInput('');
      setTokens(removeToken(ourTokens, value));
    },
    [ourTokens, setInput, setTokens]
  );

  const clearFilter = useCallback(() => {
    setTokens([]);
    setInput('');
  }, [setTokens, setInput]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Tab') {
        cancelPendingFilter();
        if (input !== '') {
          addOption(input);
        } else {
          triggerFilter(ourTokens);
        }
        e.preventDefault();
      }
    },
    [input, addOption, triggerFilter, ourTokens, cancelPendingFilter]
  );

  const handleInputChange = useCallback(
    (inputValue: string, { action }: { action: string }) => {
      if (action === 'input-change') {
        setInput(inputValue);
        if (!disableAutoFilter) {
          scheduleFilter([...ourTokens, { value: inputValue, label: inputValue }]);
        }
      }
    },
    [setInput, disableAutoFilter, scheduleFilter, ourTokens]
  );

  const handleChange = useCallback(
    (
      elems: readonly Option[],
      { action, removedValue }: { action: string; removedValue?: Option }
    ) => {
      // Any token-list mutation supersedes a keystroke-scheduled filter — drop
      // the pending timer so it can't reapply the stale token list afterward.
      cancelPendingFilter();
      const mutableElems = [...elems];
      switch (action) {
        case 'remove-value':
          if (removedValue) {
            removeOption(removedValue.value);
          }
          return;
        case 'pop-value':
          if (ourTokens.length) {
            removeOption(undefined);
          }
          return;
        case 'clear':
          clearFilter();
          return;
        default:
          break;
      }
      const last = mutableElems.pop();
      if (last?.value) {
        addOption(last.value);
      }
    },
    [removeOption, ourTokens, clearFilter, addOption, cancelPendingFilter]
  );

  const handleSubmitClick = useCallback(() => {
    cancelPendingFilter();
    if (input === '') {
      triggerFilter(ourTokens);
    } else {
      addOption(input);
    }
  }, [input, triggerFilter, ourTokens, addOption, cancelPendingFilter]);

  return (
    <>
      <div className='filter-bar-component'>
        <div className='filter-bar-select-container'>
          <CreatableSelect
            classNamePrefix='filter-bar-select'
            components={{ DropdownIndicator: null }}
            inputValue={input}
            isMulti
            menuIsOpen={false}
            placeholder={placeholder}
            value={ourTokens}
            onKeyDown={handleKeyDown}
            onInputChange={handleInputChange}
            onChange={handleChange}
            styles={{ container: base => ({ ...base, flexGrow: 1 }) }}
          />
        </div>
      </div>
      <IconButton className='filter-submit' onClick={handleSubmitClick} size={size}>
        <MdSearch className='search-icon' />
      </IconButton>
    </>
  );
};
